import type {
  LabelDisplayedRowsArgs,
  TablePaginationProps,
} from "@mui/material";
import {
  Pagination as MuiPagination,
  Skeleton,
  TablePagination,
  useEventCallback,
  PaginationItem,
} from "@mui/material";
import type { GridSlotProps } from "@mui/x-data-grid";
import {
  GRID_DEFAULT_LOCALE_TEXT,
  gridRowsLoadingSelector,
  useGridApiContext,
  useGridSelector,
  gridPageCountSelector,
  useGridRootProps,
} from "@mui/x-data-grid";
import { Icon } from "@trimble/modus-cmx";
import type { Ref } from "react";
import { useMemo } from "react";
import { Trans } from "react-i18next";

const NextIcon = () => <Icon name="chevron_right" />;
const PreviousIcon = () => <Icon name="chevron_left" />;
const FirstIcon = () => <Icon name="chevron_double_left" />;
const LastIcon = () => <Icon name="chevron_double_right" />;

const loadingDisplayedRows = () => <Skeleton width={120} />;

const displayedRows = (params: LabelDisplayedRowsArgs) => (
  <Trans
    i18nKey="data_grid.pagination_displayed_rows"
    values={{
      from: params.from,
      to: params.to,
      count: params.count,
    }}
    components={{ strong: <strong /> }}
  />
);

function Pagination({
  page,
  onPageChange,
  className,
}: Pick<TablePaginationProps, "page" | "onPageChange" | "className">) {
  const apiRef = useGridApiContext();
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);

  return (
    <MuiPagination
      color="primary"
      className={className}
      count={pageCount}
      page={page + 1}
      onChange={(event, newPage) => {
        onPageChange(event as React.MouseEvent<HTMLButtonElement>, newPage - 1);
      }}
      boundaryCount={0}
      siblingCount={2}
      hideNextButton={page >= pageCount - 1}
      hidePrevButton={page === 0}
      showFirstButton={page > 0}
      showLastButton={page < pageCount - 1}
      shape="rounded"
      renderItem={(item) => (
        <PaginationItem
          slots={{
            previous: PreviousIcon,
            next: NextIcon,
            first: FirstIcon,
            last: LastIcon,
          }}
          {...item}
        />
      )}
    />
  );
}

const GridPagination = ({
  ref,
  ...props
}: GridSlotProps["basePagination"] & { ref?: Ref<HTMLDivElement> }) => {
  const { onRowsPerPageChange, disabled, ...rest } = props;
  const computedProps = useMemo(() => {
    if (!disabled) {
      return undefined;
    }
    return {
      backIconButtonProps: { disabled: true },
      nextIconButtonProps: { disabled: true },
    };
  }, [disabled]);

  const apiRef = useGridApiContext();
  const isLoading = useGridSelector(apiRef, gridRowsLoadingSelector);
  const { localeText: localeTextOverrides } = useGridRootProps();
  const localeText = { ...GRID_DEFAULT_LOCALE_TEXT, ...localeTextOverrides };

  return (
    <TablePagination
      component="div"
      ActionsComponent={Pagination}
      onRowsPerPageChange={useEventCallback(
        (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
          onRowsPerPageChange?.(Number(event.target.value));
        }
      )}
      labelRowsPerPage={localeText.paginationRowsPerPage}
      labelDisplayedRows={isLoading ? loadingDisplayedRows : displayedRows}
      getItemAriaLabel={localeText.paginationItemAriaLabel}
      {...computedProps}
      {...rest}
      ref={ref}
    />
  );
};

export default GridPagination;
